import { datadogAdapter } from "./adapters/datadog";
import { guard } from "./guard";
import type {
  LegacyTelemetryUser,
  TelemetryAttributes,
  TelemetryUser,
} from "./types";

/**
 * The app's telemetry facade (DHT-5398). Import telemetry from here, never a
 * vendor SDK: a lint rule refuses `@datadog/*` and `expo-datadog` outside
 * `Common/Telemetry/adapters/`.
 *
 * Datadog is the only adapter. Every call is safe before `initTelemetry()` and
 * when the SDK throws or rejects (`guard`).
 *
 * Keep this module a LEAF: it may import its adapter and nothing else from the
 * app. `Common/Utils/logger` reaches it, and `Common/Storage` imports the
 * logger at boot, so importing the telemetry level, its store or the init code
 * from here would close a cycle through Storage.
 */

export type {
  LegacyTelemetryUser,
  TelemetryAttributes,
  TelemetryUser,
} from "./types";
export { log } from "./log";

/** A RUM custom action. */
export const trackAction = (
  name: string,
  attributes?: TelemetryAttributes,
): void => {
  guard(() => datadogAdapter.addAction(name, attributes ?? {}));
};

/**
 * A RUM error. RUM errors are the only client-side signal indexed in prod:
 * logger.warn is suppressed there (logger.ts shouldLog) and the web log path
 * is console-only, so anything that must be visible in Datadog goes here.
 */
export const trackError = (
  message: string,
  attributes?: TelemetryAttributes,
): void => {
  guard(() => datadogAdapter.addError(message, attributes ?? {}));
};

/**
 * Records a failure the user or their input caused (a rejected email, a
 * duplicate record, a refusal by design) as a countable RUM custom action
 * rather than a RUM error, so the error stream keeps only what is broken.
 *
 * The action carries `fault: "client"`, spread last so a caller cannot
 * override it; query it as `@type:action @action.name:<name>
 * @context.fault:client`. Never use `logger.warn` for this instead: warn is
 * dropped in production (logger.ts `shouldLog`), so the signal would vanish.
 * Keep user text (messages, input values) out of `attributes`.
 */
export const addClientFaultAction = (
  name: string,
  attributes?: TelemetryAttributes,
): void => {
  trackAction(name, { ...attributes, fault: "client" });
};

/**
 * An exception caught in app code. Web records it as a RUM error carrying
 * `message`; native writes it to Datadog Logs as an error line. That split is
 * what the old exception wrapper did, and it is kept.
 */
export const captureException = (
  error: Error | string,
  message?: string,
): void => {
  guard(() => datadogAdapter.captureException(error, message));
};

/**
 * Identifies the session's staff user by ids only: user id, user type and
 * tenant id. Any other key a caller passes is dropped here.
 */
export const identify = (user: TelemetryUser): void => {
  const { id, userType, tenantId } = user;
  guard(() => datadogAdapter.setUser({ id, userType, tenantId }));
};

/**
 * @deprecated Today's payload with name, email and clinic name, used only
 * while `Telemetry_IdsOnlyIdentify` is OFF. Removed with the flag.
 */
export const identifyLegacy = (user: LegacyTelemetryUser): void => {
  guard(() => datadogAdapter.setUserLegacy(user));
};

/** Starts a manual RUM view (native screens; a no-op on web). */
export const startView = (
  key: string,
  name: string,
  attributes?: TelemetryAttributes,
): void => {
  guard(() => datadogAdapter.startView(key, name, attributes));
};

/** Stops a manual RUM view (native screens; a no-op on web). */
export const stopView = (key: string): void => {
  guard(() => datadogAdapter.stopView(key));
};

/** Reports a feature-flag evaluation under its LaunchDarkly key, verbatim. */
export const addFeatureFlagEvaluation = (
  flagKey: string,
  value: unknown,
): void => {
  guard(() => datadogAdapter.addFeatureFlagEvaluation(flagKey, value));
};
